import { GlobalStyles, useMediaQuery, useTheme } from "@mui/material";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect, useRef } from "react";

import { encampBrandColors } from "../../../../colors";
import { SCOUT_LAUNCH_TOUR_STEPS } from "./scoutLaunchAnnouncementConfig";

/** driver.js hard-codes the overlay at z-index 10000; temporary MUI Drawer is ~1300. */
const DRIVER_OVERLAY_Z_INDEX = 10_000;
const MOBILE_NAV_ABOVE_OVERLAY_Z_INDEX = DRIVER_OVERLAY_Z_INDEX + 1;

const MOBILE_NAV_TOUR_CLASS = "scout-launch-tour--mobile-nav";
const SCOUT_NAV_ANCHOR_ID = "scout-nav";
const OPEN_NAV_ARIA_LABEL = "Open navigation";
const WAIT_FOR_NAV_TIMEOUT_MS = 2_000;
const WAIT_FOR_NAV_POLL_MS = 50;

type ScoutLaunchTourProps = {
  onDismiss: () => void;
  onComplete: () => void;
  onStepViewed: (step: 1 | 2) => void;
};

function openMobileNavDrawerIfNeeded() {
  if (document.getElementById(SCOUT_NAV_ANCHOR_ID)) {
    return;
  }
  const menuButton = document.querySelector<HTMLButtonElement>(
    `[aria-label="${OPEN_NAV_ARIA_LABEL}"]`
  );
  menuButton?.click();
}

function isScoutNavReadyForHighlight(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  // Temporary drawer slides in from the left; wait until the row is on-screen.
  return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth;
}

function waitForScoutNavElement(): Promise<HTMLElement | null> {
  const existing = document.getElementById(SCOUT_NAV_ANCHOR_ID);
  if (existing && isScoutNavReadyForHighlight(existing)) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();

    const poll = () => {
      const element = document.getElementById(SCOUT_NAV_ANCHOR_ID);
      if (element && isScoutNavReadyForHighlight(element)) {
        resolve(element);
        return;
      }
      if (Date.now() - startedAt >= WAIT_FOR_NAV_TIMEOUT_MS) {
        resolve(element);
        return;
      }
      window.setTimeout(poll, WAIT_FOR_NAV_POLL_MS);
    };

    poll();
  });
}

function setMobileNavTourStacking(enabled: boolean) {
  document.documentElement.classList.toggle(MOBILE_NAV_TOUR_CLASS, enabled);
}

function tourStepFromIndex(index: number): 1 | 2 | null {
  if (index === 0) {
    return 1;
  }
  if (index === 1) {
    return 2;
  }
  return null;
}

function buildDriveSteps(isMobileDrawerMode: boolean): DriveStep[] {
  return SCOUT_LAUNCH_TOUR_STEPS.map((step) => ({
    element: `#${step.anchorId}`,
    popover: {
      title: step.title,
      description: step.body,
      // Full-bleed temporary drawers leave no room to the right of the nav row.
      side: step.anchorId === SCOUT_NAV_ANCHOR_ID && isMobileDrawerMode ? "bottom" : step.placement,
      align: "start",
      showButtons: ["next"],
      nextBtnText: "Got it",
      doneBtnText: "Got it",
      showProgress: true,
      progressText: `${step.step} of ${SCOUT_LAUNCH_TOUR_STEPS.length}`,
    },
  }));
}

export function ScoutLaunchTour({ onDismiss, onComplete, onStepViewed }: ScoutLaunchTourProps) {
  const theme = useTheme();
  const isMobileDrawerMode = useMediaQuery(theme.breakpoints.down("md"));
  const completedRef = useRef(false);
  const advancingToNavRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  const onCompleteRef = useRef(onComplete);
  const onStepViewedRef = useRef(onStepViewed);

  useEffect(() => {
    onDismissRef.current = onDismiss;
    onCompleteRef.current = onComplete;
    onStepViewedRef.current = onStepViewed;
  }, [onDismiss, onComplete, onStepViewed]);

  useEffect(() => {
    let destroyedByCleanup = false;
    completedRef.current = false;
    advancingToNavRef.current = false;

    const driverObj = driver({
      animate: true,
      allowClose: true,
      overlayColor: encampBrandColors.richBlack,
      overlayOpacity: 0.55,
      stagePadding: 4,
      stageRadius: 12,
      popoverOffset: 12,
      popoverClass: "scout-launch-tour-popover",
      disableActiveInteraction: true,
      steps: buildDriveSteps(isMobileDrawerMode),
      onHighlighted: (_element, _step, { driver: activeDriver }) => {
        const tourStep = tourStepFromIndex(activeDriver.getActiveIndex() ?? -1);
        if (tourStep) {
          onStepViewedRef.current(tourStep);
        }
      },
      onNextClick: (_element, _step, { driver: activeDriver }) => {
        if (activeDriver.isLastStep()) {
          completedRef.current = true;
          activeDriver.destroy();
          return;
        }

        const activeIndex = activeDriver.getActiveIndex() ?? 0;
        const advancingToScoutNav = activeIndex === 0;

        if (!isMobileDrawerMode || !advancingToScoutNav || advancingToNavRef.current) {
          activeDriver.moveNext();
          return;
        }

        // Open the temporary drawer and wait for #scout-nav before highlighting.
        // Otherwise driver.js falls back to a center dummy and refresh() keeps that
        // dummy — and the drawer stays under the overlay (z-index ~1300 vs 10000).
        advancingToNavRef.current = true;
        openMobileNavDrawerIfNeeded();
        void waitForScoutNavElement().then((element) => {
          advancingToNavRef.current = false;
          if (destroyedByCleanup || !activeDriver.isActive()) {
            return;
          }
          if (element) {
            setMobileNavTourStacking(true);
          }
          activeDriver.moveNext();
        });
      },
      onCloseClick: (_element, _step, { driver: activeDriver }) => {
        activeDriver.destroy();
      },
      onDestroyed: () => {
        setMobileNavTourStacking(false);
        if (destroyedByCleanup) {
          return;
        }
        if (completedRef.current) {
          onCompleteRef.current();
          return;
        }
        onDismissRef.current();
      },
    });

    // Wait a frame so Dialog teardown has finished and anchors are measurable.
    const frameId = window.requestAnimationFrame(() => {
      driverObj.drive();
    });

    return () => {
      destroyedByCleanup = true;
      window.cancelAnimationFrame(frameId);
      setMobileNavTourStacking(false);
      driverObj.destroy();
    };
  }, [isMobileDrawerMode]);

  return (
    <GlobalStyles
      styles={{
        // Temporary Drawer mounts in a Modal (~z-index 1300) under driver.js's overlay
        // (10000). Raise the drawer for step 2 so #scout-nav is visible through the stage.
        [`html.${MOBILE_NAV_TOUR_CLASS} .MuiDrawer-root`]: {
          zIndex: MOBILE_NAV_ABOVE_OVERLAY_Z_INDEX,
        },
        // Driver overlay already dims the page; hide the Drawer backdrop to avoid double dim.
        [`html.${MOBILE_NAV_TOUR_CLASS} .MuiDrawer-root > .MuiBackdrop-root`]: {
          opacity: 0,
          pointerEvents: "none",
        },
        ".scout-launch-tour-popover": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 8,
          maxWidth: 320,
          boxShadow: theme.shadows[8],
        },
        ".scout-launch-tour-popover .driver-popover-title": {
          fontSize: 16,
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
        ".scout-launch-tour-popover .driver-popover-description": {
          fontSize: 14,
          color: theme.palette.text.secondary,
        },
        ".scout-launch-tour-popover .driver-popover-progress-text": {
          fontSize: 12,
          color: theme.palette.text.secondary,
        },
        ".scout-launch-tour-popover .driver-popover-next-btn": {
          backgroundColor: "transparent",
          color: encampBrandColors.sapphire,
          border: `1px solid ${encampBrandColors.sapphire}`,
          borderRadius: 4,
          textShadow: "none",
          fontSize: 13,
          fontWeight: 500,
          padding: "4px 12px",
        },
        ".scout-launch-tour-popover .driver-popover-next-btn:hover": {
          backgroundColor: "rgba(15, 95, 107, 0.08)",
        },
        ".scout-launch-tour-popover .driver-popover-close-btn": {
          color: theme.palette.text.secondary,
        },
        ".driver-active-element": {
          outline: `2px solid ${encampBrandColors.pumpkin} !important`,
          borderRadius: "8px",
        },
      }}
    />
  );
}
